import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';
import Image from './AppImage';
import Button from './ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { comparisonService } from '../services/comparisonService';

const ComparisonModal = ({ isOpen, onClose, products = [], category = null }) => {
  const { user } = useAuth();
  const [comparisonHistory, setComparisonHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);

  // Define displayProducts at component level to avoid hoisting issues
  const maxProducts = 3;
  const displayProducts = products?.slice(0, maxProducts);

  // Load comparison history when modal opens
  useEffect(() => {
    if (isOpen && user) {
      loadComparisonHistory();
    }
  }, [isOpen, user]);

  const loadComparisonHistory = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await comparisonService?.getUserComparisons(user?.id, category);
      if (error) {
        setError(error?.message);
      } else {
        setComparisonHistory(data || []);
      }
    } catch (err) {
      setError('Fout bij laden van vergelijkingsgeschiedenis');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveComparison = async () => {
    if (!user) {
      setError('Log in om vergelijkingen op te slaan');
      return;
    }

    if (displayProducts?.length === 0) {
      setError('Geen producten om op te slaan');
      return;
    }

    setSaveLoading(true);
    setError(null);
    try {
      const { data, error } = await comparisonService?.saveComparison(
        user?.id,
        displayProducts,
        category
      );
      
      if (error) {
        setError(error?.message);
      } else {
        // Reload history to show new comparison
        await loadComparisonHistory();
        setError(null);
        // Show success message briefly
        const successMsg = 'Vergelijking opgeslagen!';
        setError(successMsg);
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      setError('Fout bij opslaan van vergelijking');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLoadComparison = (comparison) => {
    // This would require a callback to parent to update products
    // For now, just close history view
    setShowHistory(false);
  };

  const handleDeleteComparison = async (comparisonId) => {
    if (!user) return;
    
    try {
      const { error } = await comparisonService?.deleteComparison(comparisonId);
      if (error) {
        setError(error?.message);
      } else {
        // Reload history
        await loadComparisonHistory();
      }
    } catch (err) {
      setError('Fout bij verwijderen van vergelijking');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.3 }}
                className="relative w-full max-w-7xl bg-white rounded-2xl shadow-2xl overflow-hidden"
              >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-b border-border/50 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <motion.div
                        animate={{
                          rotate: [0, 10, -10, 0],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        <Icon name="GitCompare" size={28} className="text-primary" />
                      </motion.div>
                      <div>
                        <h2 className="text-2xl font-heading font-bold text-foreground">Product Vergelijking</h2>
                        <p className="text-sm text-muted-foreground">Vergelijk specificaties, prijzen en beoordelingen</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {user && (
                        <>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowHistory(!showHistory)}
                            className="flex items-center space-x-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors"
                          >
                            <Icon name="History" size={18} className="text-primary" />
                            <span className="text-sm font-medium text-primary">Geschiedenis</span>
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleSaveComparison}
                            disabled={saveLoading || displayProducts?.length === 0}
                            className="flex items-center space-x-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:bg-gray-300 rounded-lg transition-colors"
                          >
                            <Icon name="Save" size={18} className="text-white" />
                            <span className="text-sm font-medium text-white">
                              {saveLoading ? 'Opslaan...' : 'Opslaan'}
                            </span>
                          </motion.button>
                        </>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <Icon name="X" size={24} className="text-muted-foreground" />
                      </motion.button>
                    </div>
                  </div>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-3 p-3 rounded-lg ${
                        error?.includes('opgeslagen') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}
                    >
                      <p className="text-sm">{error}</p>
                    </motion.div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6 overflow-x-auto">
                  {showHistory ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-heading font-bold text-foreground">Vergelijkingsgeschiedenis</h3>
                        <Button variant="outline" size="sm" onClick={() => setShowHistory(false)}>
                          <Icon name="ArrowLeft" size={16} className="mr-2" />
                          Terug naar vergelijking
                        </Button>
                      </div>
                      
                      {loading ? (
                        <div className="text-center py-12">
                          <Icon name="Loader" size={48} className="text-primary mx-auto mb-4 animate-spin" />
                          <p className="text-muted-foreground">Laden...</p>
                        </div>
                      ) : comparisonHistory?.length === 0 ? (
                        <div className="text-center py-12">
                          <Icon name="History" size={64} className="text-muted-foreground mx-auto mb-4" />
                          <p className="text-lg text-muted-foreground">Geen opgeslagen vergelijkingen</p>
                          <p className="text-sm text-muted-foreground mt-2">Sla een vergelijking op om deze later terug te vinden</p>
                        </div>
                      ) : (
                        <div className="grid gap-4">
                          {comparisonHistory?.map((comparison) => (
                            <motion.div
                              key={comparison?.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-border/50 p-5 hover:shadow-lg transition-shadow"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <h4 className="text-lg font-semibold text-foreground mb-1">
                                    {comparison?.comparison_name || 'Naamloze vergelijking'}
                                  </h4>
                                  <p className="text-sm text-muted-foreground">
                                    {new Date(comparison?.created_at)?.toLocaleDateString('nl-NL', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                  {comparison?.category && (
                                    <span className="inline-block mt-2 px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                                      {comparison?.category}
                                    </span>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleDeleteComparison(comparison?.id)}
                                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Icon name="Trash2" size={18} className="text-red-500" />
                                </button>
                              </div>
                              <div className="flex items-center space-x-3 overflow-x-auto pb-2">
                                {comparison?.products?.map((product, idx) => (
                                  <div key={idx} className="flex-shrink-0 bg-white rounded-lg border border-border/30 p-3 min-w-[200px]">
                                    <p className="text-sm font-semibold text-foreground line-clamp-1">
                                      {product?.brandName || product?.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground line-clamp-1">
                                      {product?.name}
                                    </p>
                                    {product?.price && (
                                      <p className="text-sm font-bold text-primary mt-1">{product?.price}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {displayProducts?.length === 0 ? (
                        <div className="text-center py-12">
                          <Icon name="Package" size={64} className="text-muted-foreground mx-auto mb-4" />
                          <p className="text-lg text-muted-foreground">Geen producten geselecteerd voor vergelijking</p>
                          <p className="text-sm text-muted-foreground mt-2">Voeg producten toe om ze te vergelijken</p>
                        </div>
                      ) : (
                        <div className="grid gap-6" style={{ gridTemplateColumns: `repeat(${displayProducts?.length}, minmax(300px, 1fr))` }}>
                          {displayProducts?.map((product, index) => (
                            <motion.div
                              key={product?.id || index}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="relative"
                            >
                              <motion.div
                                whileHover={{ y: -8, scale: 1.02 }}
                                className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-border/50 overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300"
                                style={{ transformStyle: 'preserve-3d' }}
                              >
                                {/* Product Image */}
                                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                                  <Image
                                    src={product?.image}
                                    alt={product?.imageAlt || `${product?.name} product afbeelding`}
                                    className="w-full h-full object-cover"
                                  />
                                  
                                  {/* Rating Badge */}
                                  <motion.div
                                    whileHover={{ scale: 1.1 }}
                                    className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg"
                                  >
                                    <div className="flex items-center space-x-1">
                                      <Icon name="Star" size={16} className="text-warning fill-current" />
                                      <span className="text-sm font-bold text-foreground">{product?.rating || 'N/A'}</span>
                                      {product?.reviewCount && (
                                        <span className="text-xs text-muted-foreground">({product?.reviewCount})</span>
                                      )}
                                    </div>
                                  </motion.div>

                                  {/* Type Badge */}
                                  <div className="absolute top-3 left-3 bg-primary/90 backdrop-blur-sm rounded-lg px-3 py-1 shadow-lg">
                                    <span className="text-xs font-semibold text-white">{product?.type || 'Product'}</span>
                                  </div>
                                </div>

                                {/* Product Info */}
                                <div className="p-5">
                                  {/* Brand & Name */}
                                  <div className="mb-4">
                                    {product?.brandName && (
                                      <p className="text-sm font-semibold text-primary mb-1">{product?.brandName}</p>
                                    )}
                                    <h3 className="text-xl font-heading font-bold text-foreground line-clamp-2">
                                      {product?.name || 'Onbekend Product'}
                                    </h3>
                                  </div>

                                  {/* Price */}
                                  <div className="mb-4 pb-4 border-b border-border/50">
                                    <p className="text-xs text-muted-foreground mb-1">Prijs vanaf</p>
                                    <p className="text-3xl font-bold text-primary">{product?.price || 'Op aanvraag'}</p>
                                  </div>

                                  {/* Specifications */}
                                  <div className="space-y-3 mb-4">
                                    <p className="text-sm font-semibold text-foreground mb-2">Specificaties</p>
                                    {product?.specs && product?.specs?.length > 0 ? (
                                      product?.specs?.map((spec, specIndex) => (
                                        <motion.div
                                          key={specIndex}
                                          whileHover={{ x: 5 }}
                                          className="flex items-start space-x-3 p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                                        >
                                          <Icon name={spec?.icon || 'Check'} size={16} className="text-primary mt-0.5 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs text-muted-foreground">{spec?.label}</p>
                                            <p className="text-sm font-semibold text-foreground">{spec?.value}</p>
                                          </div>
                                        </motion.div>
                                      ))
                                    ) : (
                                      <p className="text-sm text-muted-foreground">Geen specificaties beschikbaar</p>
                                    )}
                                  </div>

                                  {/* Customer Reviews Summary */}
                                  {product?.reviews && product?.reviews?.length > 0 && (
                                    <div className="mb-4 pb-4 border-b border-border/50">
                                      <p className="text-sm font-semibold text-foreground mb-3">Klantbeoordelingen</p>
                                      <div className="space-y-2">
                                        {product?.reviews?.slice(0, 2)?.map((review, reviewIndex) => (
                                          <motion.div
                                            key={reviewIndex}
                                            whileHover={{ scale: 1.02 }}
                                            className="bg-gradient-to-br from-gray-50 to-white p-3 rounded-lg border border-border/30"
                                          >
                                            <div className="flex items-center space-x-2 mb-2">
                                              <div className="flex">
                                                {[...Array(5)]?.map((_, i) => (
                                                  <Icon
                                                    key={i}
                                                    name="Star"
                                                    size={12}
                                                    className={i < (review?.rating || 0) ? 'text-warning fill-current' : 'text-gray-300'}
                                                  />
                                                ))}
                                              </div>
                                              {review?.verified && (
                                                <span className="text-xs text-success flex items-center">
                                                  <Icon name="CheckCircle" size={12} className="mr-1" />
                                                  Geverifieerd
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-xs text-muted-foreground line-clamp-2">{review?.text}</p>
                                            <p className="text-xs font-medium text-foreground mt-1">
                                              {review?.name} {review?.location && `- ${review?.location}`}
                                            </p>
                                          </motion.div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Additional Info */}
                                  {(product?.specialization || product?.certifications || product?.warranty) && (
                                    <div className="space-y-2 mb-4">
                                      {product?.specialization && (
                                        <div className="flex items-start space-x-2">
                                          <Icon name="Award" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                                          <p className="text-xs text-muted-foreground">{product?.specialization}</p>
                                        </div>
                                      )}
                                      {product?.certifications && product?.certifications?.length > 0 && (
                                        <div className="flex items-start space-x-2">
                                          <Icon name="Shield" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                                          <p className="text-xs text-muted-foreground">{product?.certifications?.join(', ')}</p>
                                        </div>
                                      )}
                                      {product?.warranty && (
                                        <div className="flex items-start space-x-2">
                                          <Icon name="Clock" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                                          <p className="text-xs text-muted-foreground">{product?.warranty}</p>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* CTA Button */}
                                  <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    <Button className="w-full" size="sm">
                                      Offerte aanvragen
                                      <Icon name="ArrowRight" size={16} className="ml-2" />
                                    </Button>
                                  </motion.div>
                                </div>
                              </motion.div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-white border-t border-border/50 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {displayProducts?.length} van max {maxProducts} producten geselecteerd
                    </p>
                    <Button variant="outline" onClick={onClose}>
                      Sluiten
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ComparisonModal;
